import { createSystem, defaultConfig, defineConfig } from '@chakra-ui/react'
import { buttonRecipe } from './components/Button.recipes'

const config = defineConfig({
  conditions: {
    dark: '[data-theme="dark"] &',
    light: '[data-theme="light"] &',
  },
  theme: {
    recipes: {
      button: buttonRecipe,
    },
    tokens: {
      colors: {
        // Primary: Grass Green (matching livewello-v4)
        green: {
          50: { value: '#f0f9f3' },
          100: { value: '#dcf4e6' },
          200: { value: '#bbe8d0' },
          300: { value: '#8dd8b3' },
          400: { value: '#56c18e' },
          500: { value: '#34a374' },
          600: { value: '#22885e' },
          700: { value: '#1e6b4b' },
          800: { value: '#1d5540' },
          900: { value: '#1a4635' },
        },
        // Secondary: Soft Sky Blue (matching livewello-v4)
        blue: {
          50: { value: '#f0f7ff' },
          100: { value: '#e0efff' },
          200: { value: '#bae0ff' },
          300: { value: '#87ccff' },
          400: { value: '#4db5ff' },
          500: { value: '#2d9cff' },
          600: { value: '#1b82f5' },
          700: { value: '#1569d1' },
          800: { value: '#1755a8' },
          900: { value: '#194683' },
        },
        // Accent: Gold/Yellow
        yellow: {
          50: { value: '#fffbeb' },
          100: { value: '#fef3c7' },
          200: { value: '#fde68a' },
          300: { value: '#fcd34d' },
          400: { value: '#fbbf24' },
          500: { value: '#f59e0b' },
          600: { value: '#d97706' },
          700: { value: '#b45309' },
          800: { value: '#92400e' },
          900: { value: '#78350f' },
        },
        // Accent: Teal
        teal: {
          50: { value: '#f0fdfa' },
          100: { value: '#ccfbf1' },
          200: { value: '#99f6e4' },
          300: { value: '#5eead4' },
          400: { value: '#2dd4bf' },
          500: { value: '#14b8a6' },
          600: { value: '#0d9488' },
          700: { value: '#0f766e' },
          800: { value: '#115e59' },
          900: { value: '#134e4a' },
        },
        gray: {
          50: { value: '#fafafa' },
          100: { value: '#f5f5f5' },
          200: { value: '#e8e8e8' },
          300: { value: '#d1d1d1' },
          400: { value: '#b0b0b0' },
          500: { value: '#888888' },
          600: { value: '#6d6d6d' },
          700: { value: '#5a5a5a' },
          800: { value: '#3d3d3d' },
          900: { value: '#2d2d2d' },
        },
        // V3 Legacy colors for logo
        v3: {
          green: { value: '#b2c230' },
          greenHover: { value: '#a1af2a' },
        },
        brand: {
          50: { value: '{colors.green.50}' },
          100: { value: '{colors.green.100}' },
          200: { value: '{colors.green.200}' },
          300: { value: '{colors.green.300}' },
          400: { value: '{colors.green.400}' },
          500: { value: '{colors.green.500}' },
          600: { value: '{colors.green.600}' },
          700: { value: '{colors.green.700}' },
          800: { value: '{colors.green.800}' },
          900: { value: '{colors.green.900}' },
        },
        accent: {
          50: { value: '{colors.blue.50}' },
          100: { value: '{colors.blue.100}' },
          200: { value: '{colors.blue.200}' },
          300: { value: '{colors.blue.300}' },
          400: { value: '{colors.blue.400}' },
          500: { value: '{colors.blue.500}' },
          600: { value: '{colors.blue.600}' },
          700: { value: '{colors.blue.700}' },
          800: { value: '{colors.blue.800}' },
          900: { value: '{colors.blue.900}' },
        },
      },
      fonts: {
        heading: { value: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
        body: { value: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
        brand: { value: '"Nunito", "Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
      },
      fontWeights: {
        normal: { value: '400' },
        medium: { value: '500' },
        semibold: { value: '600' },
        bold: { value: '700' },
      },
      lineHeights: {
        shorter: { value: '1.25' },
        short: { value: '1.375' },
        base: { value: '1.5' },
        tall: { value: '1.625' },
        taller: { value: '2' },
      },
      letterSpacings: {
        tighter: { value: '-0.05em' },
        tight: { value: '-0.025em' },
        normal: { value: '0' },
        wide: { value: '0.025em' },
        wider: { value: '0.05em' },
        widest: { value: '0.1em' },
      },
      radii: {
        none: { value: '0' },
        sm: { value: '0.125rem' },
        base: { value: '0.375rem' },
        md: { value: '0.5rem' },
        lg: { value: '0.75rem' },
        xl: { value: '1rem' },
        '2xl': { value: '1.5rem' },
        '3xl': { value: '2rem' },
        full: { value: '9999px' },
      },
      shadows: {
        xs: { value: '0 0 0 1px rgba(0, 0, 0, 0.05)' },
        sm: { value: '0 1px 2px 0 rgba(0, 0, 0, 0.03)' },
        base: { value: '0 1px 3px 0 rgba(0, 0, 0, 0.06), 0 1px 2px 0 rgba(0, 0, 0, 0.04)' },
        md: { value: '0 4px 6px -1px rgba(0, 0, 0, 0.06), 0 2px 4px -1px rgba(0, 0, 0, 0.03)' },
        lg: { value: '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.04)' },
        xl: { value: '0 20px 25px -5px rgba(0, 0, 0, 0.12), 0 10px 10px -5px rgba(0, 0, 0, 0.06)' },
        '2xl': { value: '0 25px 50px -12px rgba(0, 0, 0, 0.18)' },
        inner: { value: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.04)' },
      },
      zIndex: {
        hide: { value: -1 },
        base: { value: 0 },
        docked: { value: 10 },
        dropdown: { value: 1000 },
        sticky: { value: 1100 },
        banner: { value: 1200 },
        overlay: { value: 1300 },
        modal: { value: 1400 },
        popover: { value: 1500 },
        skipLink: { value: 1600 },
        toast: { value: 1700 },
        tooltip: { value: 1800 },
      },
    },
    semanticTokens: {
      colors: {
        bg: {
          value: { base: '{colors.gray.50}', _dark: '{colors.gray.900}' },
        },
        'bg.subtle': {
          value: { base: '{colors.white}', _dark: '{colors.gray.800}' },
        },
        fg: {
          value: { base: '{colors.gray.900}', _dark: '{colors.gray.50}' },
        },
        'fg.muted': {
          value: { base: '{colors.gray.600}', _dark: '{colors.gray.400}' },
        },
        border: {
          value: { base: '{colors.gray.200}', _dark: '{colors.gray.700}' },
        },
      },
    },
  },
  globalCss: {
    html: {
      // Set default color palette so Chakra components (Button, Tag, etc.) pick up brand colors automatically
      colorPalette: 'brand',
    },
    'html, body': {
      bg: 'bg',
      color: 'fg',
      fontFamily: 'body',
      lineHeight: 'base',
    },
    '*::placeholder': {
      opacity: 1,
      color: 'fg.muted',
    },
    // Merge global selectors under a single rule to avoid duplicate keys
    '*, *::before, *::after': {
      borderColor: 'border',
    },
    // Ensure form controls are selectable
    'input, textarea, select, [contenteditable]': {
      userSelect: 'auto',
    },
    // Ensure all text content is selectable
    'p, span, div, h1, h2, h3, h4, h5, h6, li, td, th, label, a': {
      userSelect: 'auto',
    },
  },
})

export const system = createSystem(defaultConfig, config)
